// The node on this computer, in the desktop app (HRTH-103, apps/tauri/src/node.rs): the app
// runs the `hrth node` it bundles, set up from Settings › Your storage, in the background.
import { appInvoke, isApp } from '../platform'

/** What the app's `hrth node status --json` says. */
export interface DesktopNode {
  /** Where the node's settings live on this computer. */
  home: string
  paired: boolean
  server: string | null
  location: string | null
  libraries: { id: string; path: string }[]
  /** Where it keeps what people upload to it. */
  store: string | null
  /** `active`, `inactive`, … or `not installed`; null where there's no service manager. */
  service: string | null
  at: StartAt | null
}

/** When the background node starts: at sign-in, or at boot (asks for an admin password). */
export type StartAt = 'login' | 'boot'

/** Whether this is the desktop app, which can make this computer a node. */
export async function nodeSupported(): Promise<boolean> {
  if (!isApp) return false
  try {
    return await appInvoke<boolean>('node_supported')
  } catch {
    return false
  }
}

export const nodeStatus = () => appInvoke<DesktopNode>('node_status')
export const nodePair = (link: string) => appInvoke<void>('node_pair', { link })
export const nodePickFolder = () => appInvoke<string | null>('node_pick_folder')
export const nodeShare = (path: string, name: string) => appInvoke<string>('node_share', { path, name })
export const nodeService = (action: 'install' | 'start' | 'stop' | 'uninstall', at?: StartAt) =>
  appInvoke<DesktopNode>('node_service', { action, at })
export const nodeUnpair = () => appInvoke<void>('node_unpair')

/** A folder's own name, from its path on any system. */
export const folderName = (path: string) => path.replace(/[\\/]+$/, '').split(/[\\/]/).pop() || 'Folder'

/** In a few words: whether it's serving, and when it starts. */
export function serviceText(n: DesktopNode): string {
  if (!n.service || n.service === 'not installed') return 'not running in the background'
  const state = n.service === 'active' ? 'running' : n.service === 'inactive' ? 'stopped' : n.service
  return `${state} · starts ${n.at === 'boot' ? 'at boot' : 'when you sign in'}`
}
